import { altitudeToPressure } from './altitude.js';

// Skysight's exact cumulus cloud-icon path (decoded from bundle).
// Natural extent: x ∈ [6, 94] (width 88), y ∈ [20, 80] (height 60).
const SKYSIGHT_CLOUD_PATH =
    'm 58,20 c 12.6839,0 21.9728,10.07677 23,21.3125 ' +
    '7.5889,2.84531 13,10.13863 13,18.68753 0,11.0101 -8.9898,20 -20,20 ' +
    'l -48,0 c -11.4806,0 -20,-8.0301 -20,-18 ' +
    '0,-7.6315 4.8283,-15.01102 12.2812,-17.37503 ' +
    '0.6825,-5.73952 3.6227,-10.27984 7.6876,-12.71875 ' +
    '3.8639,-2.31835 8.5212,-2.77653 12.8437,-1.46875 ' +
    '3.8646,-5.86362 10.5659,-10.4375 19.1875,-10.4375 z';

/**
 * Render a row of cumulus cloud icons in the cloud-axis area, matching
 * Skysight's drawCumulusCloudWithPressure(lclPressure, pblAltitude):
 *
 *   - Number of icons = round(cu_oktas), clamped [0, 8].
 *   - Tiled horizontally over a 120px range starting at x(tMin)+10.
 *   - Each icon vertically spans the layer between LCL altitude and PBL top
 *     altitude (i.e. the cumulus layer thickness).
 *   - Style: white fill rgba(255,255,255,0.95), gray stroke rgba(100,100,100,0.8),
 *     1px non-scaling-stroke so outlines stay crisp at any chart size.
 *   - For n >= 6, icons are drawn in a 3-pass interleaved order (0,3,6,...
 *     then 1,4,7,... then 2,5,8,...) so adjacent icons overlap with visual
 *     variety (like a real cumulus field).
 *
 * Source: docs/skysight-skewt-bundle.js drawCumulusCloudWithPressure.
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array} data - sounding data; data[0] holds surface cu_oktas/pblh
 * @param {number} plcl - LCL pressure in hPa (from liftParcel)
 */
export function drawCumulusStack(container, scales, data, plcl) {
    container.selectAll('.skewt-ext-cumulus').remove();
    if (!data || data.length === 0) return;
    if (!Number.isFinite(plcl)) return;

    const surface = data[0];
    const oktasRaw = surface.cu_oktas;
    const pblh = surface.pblh;
    if (!Number.isFinite(oktasRaw) || !Number.isFinite(pblh)) return;
    const n = Math.min(Math.max(Math.round(oktasRaw), 0), 8);
    if (n === 0) return;

    // LCL pressure is the "base" (lower altitude → larger y on chart).
    // PBL top altitude is the "top" of the cumulus layer.
    const pblPress = altitudeToPressure(pblh, data);
    if (!Number.isFinite(pblPress)) return;

    const a = scales.y(pblPress);          // y at top of layer (smaller y)
    const u = scales.y(plcl);              // y at base of layer (larger y)
    const e = u - a;                       // layer height in pixels
    if (e < 15) return;                    // Skysight skips paper-thin layers

    // Tiling geometry (Skysight constants)
    const c = 120;                         // total horizontal tiling range
    const mMax = 25;                       // max icon width
    const yBase = scales.x(scales.tMin) + 10;
    const w = 20;
    const i = 80;
    const h = i - w;                       // 60 — icon natural vertical extent
    const o = 88;                          // icon natural width
    const f = e / h;                       // vertical scale factor
    const xScale = Math.min(1, 0.7 + f * 0.3);

    let M; // icon width after scaling
    let b; // step between icons
    if (n >= 6) {
        M = mMax * xScale;
        b = (c - M) / (n - 1);
    } else {
        M = Math.min(mMax * xScale, (c - (n - 1) * 3) / n);
        b = M + 3;
    }

    // Icon draw order: for n>=6, three interleaved passes for visual variety.
    let order;
    if (n >= 6) {
        order = [];
        for (let T = 0; T < n; T += 3) order.push(T);
        for (let T = 1; T < n; T += 3) order.push(T);
        for (let T = 2; T < n; T += 3) order.push(T);
    } else {
        order = Array.from({ length: n }, (_, idx) => idx);
    }

    const layer = container.append('g').attr('class', 'skewt-ext-cumulus');
    for (const T of order) {
        const S = yBase + T * b;
        const iconG = layer
            .append('g')
            .attr('class', 'cumulus-cloud-icon')
            .attr('transform', `translate(${S}, ${u - i * f})`);
        iconG
            .append('g')
            .attr('transform', `scale(${M / o}, ${f})`)
            .append('path')
            .attr('d', SKYSIGHT_CLOUD_PATH)
            .style('fill', 'rgba(255, 255, 255, 0.95)')
            .style('stroke', 'rgba(100, 100, 100, 0.8)')
            .style('stroke-width', '1px')
            .style('vector-effect', 'non-scaling-stroke');
    }
}
